import { inject, Lazy } from "aurelia-dependency-injection";
import { HttpClient, Interceptor } from "aurelia-http-client";
import { BindingSignaler } from "aurelia-templating-resources";
import { EventAggregator } from "aurelia-event-aggregator";
import { Authentication } from "./authentication";
import { AuthConfig } from "./auth-config";
const jsSHA = require("jssha");

@inject(
  Authentication,
  EventAggregator,
  BindingSignaler,
  Lazy.of(HttpClient),
  AuthConfig,
)
export class AuthService {
  constructor(
    private auth: Authentication,
    private eventAggregator: EventAggregator,
    private bindingSignaler: BindingSignaler,
    private httpClient: () => HttpClient,
    private config: AuthConfig,
  ) {}

  getTokenInterceptor(): Interceptor {
    return this.auth.tokenInterceptor;
  }

  isAuthenticated(): boolean {
    return this.auth.isAuthenticated();
  }

  getTokenPayload() {
    return this.auth.getTokenPayload();
  }

  getTokenAuthHeaderValue(): string | undefined {
    return this.auth.getTokenAuthHeaderValue();
  }

  hasRight(right: string): boolean {
    return this.auth.hasRightOneOf([right]);
  }

  hasRightOneOf(rights: string[]): boolean {
    return this.auth.hasRightOneOf(rights);
  }

  setToken(token: string) {
    this.auth.setToken(
      Object.defineProperty({}, this.config.tokenName, { value: token }),
    );
  }

  signup(
    signupPostData:
      | { displayName: string; email: string; password: string }
      | any,
  ): Promise<any> {
    let signupUrl = this.auth.getSignupUrl();
    return this.httpClient()
      .post(signupUrl, signupPostData)
      .then((response) => {
        if (this.config.loginOnSignup) {
          this.auth.setToken(response.content);
        } else if (this.config.signupRedirect) {
          window.location.href = this.config.signupRedirect;
        }
        this.eventAggregator.publish("auth:signup", response.content);
        this.bindingSignaler.signal("auth:signup");
        this.bindingSignaler.signal("auth:changed");
        return response;
      });
  }

  login(
    loginPostData: { email: string; password: string } | any,
  ): Promise<any> {
    let loginUrl = this.auth.getLoginUrl();
    return this.httpClient()
      .post(loginUrl, loginPostData)
      .then((response) => {
        this.auth.setToken(response.content);
        this.eventAggregator.publish("auth:login", response.content);
        this.bindingSignaler.signal("auth:login");
        this.bindingSignaler.signal("auth:changed");
        return response;
      });
  }

  logout(redirectUri?: string): Promise<any> {
    return this.auth.logout(redirectUri).then(() => {
      this.eventAggregator.publish("auth:logout");
      this.bindingSignaler.signal("auth:logout");
      this.bindingSignaler.signal("auth:changed");
    });
  }

  changePassword(
    changePasswordPostData:
      | { username: string; oldPassword: string; newPassword: string }
      | any,
  ): Promise<any> {
    let loginUrl = this.auth.getChangePasswordUrl();
    return this.httpClient()
      .configure((h) => {
        h.withTimeout(30000);
        h.withInterceptor(this.getTokenInterceptor());
      })
      .post(loginUrl, changePasswordPostData)
      .then((response) => {
        if (response.content) this.auth.setToken(response.content);
        else this.auth.removeToken();
        this.eventAggregator.publish("auth:passwordChanged", response.content);
        this.bindingSignaler.signal("auth:passwordChanged");
        this.bindingSignaler.signal("auth:changed");
        return response;
      });
  }

  /** Utility function: hash a password with SHA-256. */
  hash(password: string): string {
    let shaObj = new jsSHA("SHA-256", "TEXT");
    shaObj.update(password);
    return shaObj.getHash("HEX");
  }
}
